import React from "react";
import { Users } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { TransformedInfluencer } from "../types";

interface InfluencersPanelProps {
  influencers: TransformedInfluencer[];
  showInfluencersTable: boolean;
  onToggleTable: () => void;
}

export const InfluencersPanel: React.FC<InfluencersPanelProps> = ({
  influencers,
  showInfluencersTable,
  onToggleTable,
}) => {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            onClick={onToggleTable}
            className={`p-6 border-t lg:border-t-0 lg:border-l border-slate-200 flex flex-col justify-between cursor-pointer group transition-colors ${
              showInfluencersTable ? "bg-slate-50" : "hover:bg-slate-50/50"
            }`}
          >
            <div>
              <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center text-orange-500 group-hover:scale-105 transition-transform">
                <Users className="w-5 h-5" />
              </div>
              <div className="text-[#6B7280] font-bold text-[12px] tracking-wider uppercase mt-5">
                Influencers
              </div>
              <div className="text-3xl font-bold text-slate-900 tracking-tight mt-1">
                {influencers.length}
              </div>
            </div>
            <div className="mt-3">
              <span className="inline-flex px-2 py-0.5 rounded bg-orange-100 text-orange-700 text-[12px] font-semibold">
                Active in campaign
              </span>
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="bg-slate-900 border-slate-800 text-white p-3 rounded-lg shadow-xl w-64 max-h-80 overflow-y-auto"
        >
          <div className="space-y-2">
            <div className="text-xs font-semibold text-slate-400 border-b border-slate-800 pb-1.5 mb-1.5">
              Influencers ({influencers.length})
            </div>
            {influencers.length > 0 ? (
              <div className="space-y-2">
                {influencers.map((inf) => (
                  <div key={inf.id} className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full overflow-hidden bg-slate-800 flex-shrink-0 flex items-center justify-center">
                      {inf.profilePicUrl ? (
                        <img
                          src={inf.profilePicUrl}
                          alt={inf.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-[10px] font-bold text-slate-300">
                          {inf.name?.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium text-slate-200 truncate">
                        {inf.name}
                      </div>
                      <div className="text-[10px] text-slate-400 truncate">
                        @{inf.handle}
                      </div>
                    </div>
                    <span className="text-[9px] text-slate-500 uppercase font-semibold">
                      {inf.platform}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-slate-500 py-1">No influencers assigned</div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
